import { Libraries, LibraryKey } from "./library.type";

export const MANIFEST_FILE_NAME = "numerous.toml" as const;
export const MANIFEST_CONVERSION_ERROR = new Error(
  "Could not convert manifest"
);
export const MANIFEST_INCOMPLETE_ERROR = new Error("Manifest is incomplete");

type ManifestV0 = {
  name: string;
  library: LibraryKey;
  python: string;
  app_file: string;
  requirements_file: string;
  port: string;
  description?: string;
  cover_image: string;
  exclude: string[];
};

type ManifestV1 = ManifestV0 & {
  port: number;
  deploy?: Deployment;
};

type DockerManifest = App & { docker: Docker };
export type PythonManifest = App & { python: Python };

export type Manifest = DockerManifest | PythonManifest;

type Deployment = {
  organization: string;
  app: string;
};

type App = {
  name: string;
  description?: string;
  cover_image: string;
  exclude: string[];
  port: number;
  deploy?: Deployment;
};

type Python = {
  library: LibraryKey | "";
  version: string;
  app_file: string;
  requirements_file: string;
};

type Docker = {
  dockerfile: string;
  context: string;
};

export type ManifestUnion = Manifest | ManifestV0 | ManifestV1;

function isValidManifest(m: ManifestUnion): m is Manifest {
  if (
    typeof m.cover_image !== "string" ||
    typeof m.exclude !== "object" ||
    !(m.exclude instanceof Array) ||
    typeof m.name !== "string" ||
    typeof m.port !== "number"
  )
    return false;

  if ("python" in m) {
    const p = m.python;
    return (
      typeof p === "object" &&
      typeof p.app_file === "string" &&
      typeof p.version === "string" &&
      typeof p.requirements_file === "string" &&
      Libraries.some(({ key }) => key === p.library)
    );
  }

  return (
    "docker" in m &&
    typeof m.docker === "object" &&
    typeof m.docker.context === "string" &&
    typeof m.docker.dockerfile === "string"
  );
}

export function isFullManifest(m: Partial<Manifest>): m is Manifest {
  if (m.name === undefined) return false;
  if (m.port === undefined) return false;
  if (m.cover_image === undefined) return false;
  if (m.exclude === undefined) return false;
  if (!("python" in m || "docker" in m)) return false;
  if ("python" in m) {
    if (m.python?.library === undefined) return false;
    if (m.python?.app_file === undefined) return false;
    if (m.python?.requirements_file === undefined) return false;
    if (m.python?.version === undefined) return false;
  }
  if ("docker" in m) {
    if (m.docker?.context === undefined) return false;
    if (m.docker?.dockerfile === undefined) return false;
  }
  return true;
}

export function isPythonManifest(m: Manifest): m is PythonManifest {
  return "python" in m && !["undefined", "string"].includes(typeof m.python);
}

function isDockerManifest(m: Manifest): m is DockerManifest {
  return "docker" in m && !["undefined", "string"].includes(typeof m.docker);
}

function isV0(m: ManifestUnion): m is ManifestV0 {
  if (m.name === undefined) return false;
  if (m.port === undefined) return false;
  if (m.cover_image === undefined) return false;
  if (m.exclude === undefined) return false;
  if ("library" in m && m.library === undefined) return false;
  if ("python" in m && typeof m.python !== "string") return false;
  if ("app_file" in m && typeof m.app_file !== "string") return false;
  if ("requirements_file" in m && typeof m.requirements_file !== "string")
    return false;
  if ("port" in m && typeof m.port !== "string") return false;

  return true;
}

function isV1(m: ManifestUnion): m is ManifestV1 {
  if (m.name === undefined) return false;
  if (m.port === undefined) return false;
  if (m.cover_image === undefined) return false;
  if (m.exclude === undefined) return false;
  if ("library" in m && m.library === undefined) return false;
  if ("python" in m && typeof m.python !== "string") return false;
  if ("app_file" in m && typeof m.app_file !== "string") return false;
  if ("requirements_file" in m && typeof m.requirements_file !== "string")
    return false;
  if ("port" in m && typeof m.port !== "number") return false;
  return true;
}

function v0ToManifest(m: ManifestV0): Manifest {
  return {
    name: m.name,
    description: m.description,
    cover_image: m.cover_image,
    exclude: m.exclude,
    port: Number(m.port),
    python: {
      library: m.library,
      app_file: m.app_file,
      requirements_file: m.requirements_file,
      version: m.python
    }
  };
}

function v1ToManifest(m: ManifestV1): Manifest {
  return {
    name: m.name,
    description: m.description,
    cover_image: m.cover_image,
    exclude: m.exclude,
    port: m.port,
    python: {
      library: m.library,
      app_file: m.app_file,
      requirements_file: m.requirements_file,
      version: m.python
    },
    deploy: m.deploy
  };
}

export function toManifest(m: ManifestUnion): [Manifest, Error | null] {
  let manifest: Manifest;

  if (isV0(m)) manifest = v0ToManifest(m);
  else if (isV1(m)) manifest = v1ToManifest(m);
  else manifest = m;

  if (isFullManifest(manifest) && !isValidManifest(manifest))
    return [manifest, MANIFEST_CONVERSION_ERROR];

  if (!isFullManifest(manifest)) return [manifest, MANIFEST_INCOMPLETE_ERROR];

  return [manifest, null];
}

export const BASE_MANIFEST: PythonManifest = {
  name: "",
  description: "",
  port: 0,
  cover_image: "",
  exclude: ["*venv", "venv*", ".git", ".env"],
  python: {
    app_file: "",
    library: "",
    requirements_file: "",
    version: ""
  }
} as const;

/**
 *
 * @param m1 highest prioritized manifest
 * @param m2 lowest prioritized manifest
 * @returns merged Manifest
 */
export function mergeManifests(m1: Manifest, m2: Manifest): Manifest {
  let python: Python = {
    library: "",
    version: "",
    app_file: "",
    requirements_file: ""
  };

  let docker: Docker = {
    dockerfile: "",
    context: ""
  };

  if (isPythonManifest(m1) || isPythonManifest(m2)) {
    const python1: Partial<Python> = isPythonManifest(m1) ? m1.python : {};
    const python2: Partial<Python> = isPythonManifest(m2) ? m2.python : {};
    python = {
      library: python1?.library || python2?.library || "",
      version: python1?.version || python2?.version || "",
      app_file: python1?.app_file || python2?.app_file || "",
      requirements_file:
        python1?.requirements_file || python2?.requirements_file || ""
    };
  } else if (isDockerManifest(m1) || isDockerManifest(m2)) {
    const docker1: Partial<Docker> = isDockerManifest(m1) ? m1.docker : {};
    const docker2: Partial<Docker> = isDockerManifest(m2) ? m2.docker : {};
    docker = {
      dockerfile: docker1?.dockerfile || docker2?.dockerfile || "",
      context: docker1?.context || docker2?.context || ""
    };
  }

  return {
    name: m1.name || m2.name,
    description: m1.description ?? m2.description,
    cover_image: m1.cover_image || m2.cover_image,
    exclude: [...new Set([...(m1.exclude || []), ...(m2.exclude || [])])],
    port: m1.port || m2.port,
    deploy: m1.deploy || m2.deploy,
    ...pythonOrDocker(m1, m2, python, docker)
  };
}

const pythonOrDocker = (
  m1: Manifest,
  m2: Manifest,
  p: Python,
  d: Docker
): { python: Python } | { docker: Docker } => {
  if (isPythonManifest(m1)) return { python: p };
  if (isDockerManifest(m1)) return { docker: d };
  if (isPythonManifest(m2)) return { python: p };
  if (isDockerManifest(m2)) return { docker: d };
  return { python: p };
};
